import { useEffect, useRef, useState } from 'react';
import { Edit, Select } from '@react-apps/design-system';
import type {
  ExamPreviewQuestion,
  QuestionBody,
} from '../../domain/entities/content.entity';
import { readQuestionBody } from '../exam-body';
import { SectionCard } from '../display';
import {
  ANNULLED_OPTION,
  answerOptions,
  correctAlternativeLetter,
  currentAnswerOption,
  isAnnulled,
} from '../answer-key';
import {
  ANSWER_KEY_ANCHOR,
  questionAnchor,
  useQuestionSpotlight,
} from '../hooks/use-question-spotlight';
import {
  addContentLock,
  removeContentLock,
  useContentLockState,
} from '../content-lock';
import { IconButton } from '@/shared/components/icon-button';
import { DismissibleHint } from './DismissibleHint';
import { EditLockNotice } from './EditLockNotice';

/** Linhas por coluna antes de quebrar para a coluna seguinte. */
const ROWS_PER_COLUMN = 10;

/** Chave da dispensa da dica de navegação. */
const HINT_KEY = 'exam-preview:answer-key-hint';

interface AnswerCell {
  position: number;
  questionId: string;
  body: QuestionBody;
  /** Letra correta, `null` para anulada, dissertativa ou corpo inconsistente. */
  letter: string | null;
  annulled: boolean;
}

/**
 * Gabarito da prova: grade `posição` + `resposta`, em colunas lado a lado.
 *
 * É também o **índice de navegação** da tela: o número de cada questão é um
 * botão que rola até ela e pisca nela. Por isso a grade inclui **toda** questão,
 * inclusive dissertativa (que não tem resposta e mostra `—`): uma questão fora
 * da grade seria uma questão sem caminho até ela.
 *
 * Cada resposta é editável no lápis, num `<select>` montado a partir das
 * alternativas **daquela** questão — mais "Anulada" no fim. Letra fora do
 * intervalo, duas letras, número: nada disso é expressável, então não há
 * validação a fazer nem mensagem de erro a mostrar.
 *
 * O lápis pede antes o lock de edição da questão, e o seletor só abre com ele.
 * Gravar, cancelar ou sair da célula solta o lock. Se outra pessoa está com a
 * questão, o aviso diz quem, acima da grade, e o lápis dela fica bloqueado.
 */
export function AnswerKeyTable({
  questions,
  onSetAnswer,
  saving,
}: {
  questions: ExamPreviewQuestion[];
  onSetAnswer: (
    input: {
      questionId: string;
      correctAlternativeId?: string;
      annulled?: boolean;
    },
    done: string,
  ) => void;
  saving: boolean;
}) {
  const { register, spotlight } = useQuestionSpotlight();
  const [editing, setEditing] = useState<string | null>(null);
  // A célula cujo lock foi pedido por último: é dela o aviso acima da grade.
  const [lockTarget, setLockTarget] = useState<AnswerCell | null>(null);
  const [acquiring, setAcquiring] = useState<string | null>(null);
  const inFlight = useRef(false);
  const lockState = useContentLockState(lockTarget?.questionId);
  const noticeId = 'answer-key-edit-lock';

  // Sair da tela com uma célula aberta solta o lock dela.
  const lockTargetRef = useRef<string | null>(null);
  useEffect(() => {
    lockTargetRef.current = lockTarget?.questionId ?? null;
  }, [lockTarget]);
  useEffect(
    () => () => {
      if (lockTargetRef.current) removeContentLock(lockTargetRef.current);
    },
    [],
  );

  const openCell = async (cell: AnswerCell) => {
    if (inFlight.current) return;
    inFlight.current = true;
    if (lockTarget && lockTarget.questionId !== cell.questionId) {
      removeContentLock(lockTarget.questionId);
    }
    setEditing(null);
    setLockTarget(cell);
    setAcquiring(cell.questionId);
    try {
      const state = await addContentLock(cell.questionId);
      if (state.status === 'held') setEditing(cell.questionId);
    } finally {
      inFlight.current = false;
      setAcquiring(null);
    }
  };

  // Fechar a célula desmonta o seletor, que tinha o foco: ele volta ao lápis
  // da célula que fechou, e não ao topo da página — numa grade de 80 questões,
  // quem usa teclado perderia a posição. Só quando o foco caiu no `body`: se a
  // célula fechou por blur, a pessoa já foi para outro lugar.
  const pencils = useRef(new Map<string, HTMLSpanElement>());
  const wasEditing = useRef<string | null>(null);
  useEffect(() => {
    const closed = wasEditing.current;
    const focusLost =
      !document.activeElement || document.activeElement === document.body;
    if (closed && editing === null && focusLost) {
      pencils.current.get(closed)?.querySelector('button')?.focus();
    }
    wasEditing.current = editing;
  }, [editing]);

  const closeCell = (questionId: string) => {
    removeContentLock(questionId);
    setEditing(null);
    setLockTarget(null);
  };

  const cells: AnswerCell[] = questions.flatMap((entry) => {
    const body = readQuestionBody(entry.question.body);
    if (!body) return [];
    return [
      {
        position: entry.position,
        questionId: entry.question.id,
        body,
        letter: correctAlternativeLetter(body),
        annulled: isAnnulled(body),
      },
    ];
  });

  if (cells.length === 0) return null;

  const columnCount = Math.ceil(cells.length / ROWS_PER_COLUMN);
  const columns = Array.from({ length: columnCount }, (_, i) =>
    cells.slice(i * ROWS_PER_COLUMN, (i + 1) * ROWS_PER_COLUMN),
  );

  return (
    <div ref={register(ANSWER_KEY_ANCHOR)}>
      <SectionCard title="Gabarito">
        <DismissibleHint storageKey={HINT_KEY}>
          Clique no número de uma questão para ir direto até ela — e no título{' '}
          <strong>Questão N</strong> para voltar ao gabarito.
        </DismissibleHint>
        {lockTarget && (
          <EditLockNotice
            id={noticeId}
            state={lockState}
            subject={`Questão ${lockTarget.position}`}
            keepsText={false}
            retrying={acquiring === lockTarget.questionId}
            onRetry={() => void openCell(lockTarget)}
            onExit={() => closeCell(lockTarget.questionId)}
          />
        )}
        <div className="flex flex-wrap gap-4">
          {columns.map((column, columnIndex) => (
            <div
              key={columnIndex}
              className="border-(--color-border) w-44 overflow-hidden rounded-lg border"
            >
              <div className="bg-(--color-bg-secondary) text-(--color-text-secondary) grid grid-cols-[3rem_1fr] text-xs font-medium uppercase tracking-wide">
                <span className="border-(--color-border) border-r px-2 py-1">
                  Nº
                </span>
                <span className="px-2 py-1">Resp.</span>
              </div>
              {column.map((cell) => (
                <div
                  key={cell.position}
                  className="border-(--color-border) text-(--color-text-primary) grid grid-cols-[3rem_1fr] border-t text-sm"
                >
                  {/* Item 4: o número é o caminho até a questão. */}
                  <button
                    type="button"
                    onClick={() => spotlight(questionAnchor(cell.position))}
                    title={`Ir para a questão ${cell.position}`}
                    className="border-(--color-border) hover:bg-(--color-bg-secondary) hover:text-(--color-accent) border-r px-2 py-1 text-left tabular-nums"
                  >
                    {cell.position}
                  </button>
                  {editing === cell.questionId &&
                  lockState?.status === 'held' ? (
                    <AnswerSelect
                      cell={cell}
                      saving={saving}
                      onClose={() => closeCell(cell.questionId)}
                      onSave={(input, done) =>
                        onSetAnswer(
                          { questionId: cell.questionId, ...input },
                          done,
                        )
                      }
                    />
                  ) : (
                    <div className="flex items-center justify-between gap-1 px-2 py-1">
                      <AnswerValue cell={cell} />
                      {cell.body.format === 'MULTIPLE_CHOICE' && (
                        <span
                          ref={(element) => {
                            if (element) {
                              pencils.current.set(cell.questionId, element);
                            } else {
                              pencils.current.delete(cell.questionId);
                            }
                          }}
                        >
                          <IconButton
                            label={`Editar a resposta da questão ${cell.position}`}
                            icon={<Edit size="xs" />}
                            disabled={acquiring === cell.questionId}
                            blockedBy={
                              lockTarget?.questionId === cell.questionId &&
                              lockState?.status === 'locked'
                                ? noticeId
                                : undefined
                            }
                            onClick={() => void openCell(cell)}
                          />
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      </SectionCard>
    </div>
  );
}

function AnswerValue({ cell }: { cell: AnswerCell }) {
  if (cell.annulled) {
    return (
      <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">
        Anulada
      </span>
    );
  }
  if (cell.letter === null) {
    return (
      <span
        className="text-(--color-text-secondary)"
        title={
          cell.body.format === 'ESSAY'
            ? 'Questão dissertativa — não tem alternativa correta'
            : 'A alternativa correta salva não existe entre as alternativas da questão'
        }
      >
        —
      </span>
    );
  }
  return <span className="font-semibold">{cell.letter}</span>;
}

/**
 * Edição da célula: um `<select>` com as alternativas da própria questão.
 *
 * Foi um input de uma letra, com validação e mensagem de recusa. O `select`
 * apaga essa camada inteira: `D` numa questão de A–C, `AB` e `2` deixam de ser
 * digitáveis, então não há o que recusar nem o que explicar. Uma questão de 3
 * alternativas e uma de 5 continuam convivendo na mesma prova — a lista é
 * montada por questão, não de um alfabeto fixo.
 *
 * "Anulada" é a última opção, e não um controle à parte: do ponto de vista do
 * gabarito é uma resposta possível da questão.
 *
 * Escolher **é** salvar — um `select` que precisasse de um botão de confirmar
 * depois somaria um clique a uma ação de um clique. Sair do campo sem escolher
 * fecha sem salvar.
 */
function AnswerSelect({
  cell,
  saving,
  onSave,
  onClose,
}: {
  cell: AnswerCell;
  saving: boolean;
  /** `done` é o aviso de sucesso — quem sabe o que mudou é quem monta a célula. */
  onSave: (
    input: { correctAlternativeId?: string; annulled?: boolean },
    done: string,
  ) => void;
  onClose: () => void;
}) {
  const commit = (value: string) => {
    // Escolher o que já estava lá não é mudança: fecha sem gravar e sem avisar.
    if (value === currentAnswerOption(cell.body)) {
      onClose();
      return;
    }
    if (value === ANNULLED_OPTION) {
      onSave(
        { annulled: true },
        `Questão ${cell.position} marcada como anulada`,
      );
    } else {
      // Escolher uma letra numa questão anulada é, na prática, desanulá-la: a
      // resposta voltou a existir.
      onSave(
        { correctAlternativeId: value, annulled: false },
        `Gabarito da questão ${cell.position}: ${value.toUpperCase()}`,
      );
    }
    onClose();
  };

  return (
    /* O `Select` do design-system, e não um `<select>` cru: o reset global do
       app aplica `appearance: none` em todo `select` (`globals.css`), então a
       seta nativa não existe — quem a desenha é este componente. Sem ele, o
       campo fica indistinguível de um input de texto, que é justamente o
       contrário do que a troca por seletor queria dizer.

       As classes encolhem o padrão (`py-3 pl-3 pr-9`) para caber na célula da
       grade; `pr-7` mantém o texto fora da seta, que fica em `right-3`. */
    <Select
      autoFocus
      disabled={saving}
      defaultValue={currentAnswerOption(cell.body)}
      onChange={(e) => commit(e.target.value)}
      onBlur={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
      aria-label={`Resposta da questão ${cell.position}`}
      className="my-0.5 py-0.5 pl-1.5 pr-7 text-sm font-semibold"
      options={answerOptions(cell.body)}
    />
  );
}
